/**
 * The pieces every money-moving create shares to survive a lost answer (wallet top-ups, wallet debits): an
 * attempt stored per organization before its request leaves, a lock every tab of the browser shares while it
 * is sent, and which failures cannot rule out that it took effect. Each caller keeps its own storage key and
 * lock name, so two kinds of attempt to the same organization never share a slot.
 */

/** What every stored attempt carries: the body sent, with its idempotency key. */
export interface StoredAttempt<P extends { idempotency_key: string }> {
  payload: P;
}

// No answer, a 5xx, a timeout or a throttle: the operation may have happened. Not a 409: every conflict these
// creates answer (a wallet that is not active, a key reused with another body) is a refusal before anything moves.
const AMBIGUOUS_STATUSES: ReadonlySet<number> = new Set([408, 429]);

/** After a failed create: true when it may still have moved money, so the attempt must be kept. */
export function mayHaveTakenEffect(status: number | null): boolean {
  return status === null || status >= 500 || AMBIGUOUS_STATUSES.has(status);
}

/**
 * Right before an attempt leaves, against what the store holds NOW — another tab may have sent one to the
 * same organization while this challenge was open, and its answer may be lost. `null` refuses: a different
 * attempt is unresolved. Otherwise the payload to send; `adopted` when it is the unresolved attempt itself
 * (same body), sent with ITS key so the backend replays it instead of moving the money a second time.
 */
export function reconcileAttemptAtSend<P extends { idempotency_key: string }>(
  stored: StoredAttempt<P> | null,
  outgoing: P,
  isSame: (stored: P, next: P) => boolean,
): { payload: P; adopted: boolean } | null {
  if (!stored || stored.payload.idempotency_key === outgoing.idempotency_key) {
    return { payload: outgoing, adopted: false };
  }
  if (!isSame(stored.payload, outgoing)) return null;
  return { payload: stored.payload, adopted: true };
}

/** How long a send waits for another tab's send to the same organization to finish. */
const LOCK_WAIT_MS = 30_000;

export type AttemptLocks = Pick<LockManager, "request">;

const browserLocks = (): AttemptLocks | null =>
  typeof navigator !== "undefined" && navigator.locks ? navigator.locks : null;

/**
 * Runs one send holding `lockName`, shared by every tab of this browser (Web Locks): a second tab waits for
 * the first request to settle, then reads the store again, so it can never send another key while the first
 * answer is still out. Waits at most 30 s, then throws `busy()`; runs unlocked where the browser has no lock
 * manager.
 */
export async function withAttemptLock<T>(
  lockName: string,
  run: () => Promise<T>,
  busy: () => Error,
  locks: AttemptLocks | null = browserLocks(),
  waitMs: number = LOCK_WAIT_MS,
): Promise<T> {
  if (!locks) return run();
  const signal = typeof AbortSignal.timeout === "function" ? AbortSignal.timeout(waitMs) : undefined;
  // The timeout only bounds the wait: once the lock is held, the request decides, however long it takes.
  let acquired = false;
  try {
    return await locks.request(lockName, { signal }, () => {
      acquired = true;
      return run();
    });
  } catch (error: unknown) {
    if (!acquired && signal?.aborted) throw busy();
    throw error;
  }
}

export type AttemptStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

/**
 * One unresolved attempt per organization, under `storageKey`. Survives closing the dialog and a reload:
 * written before the request leaves, removed only once its outcome is known. Falls back to memory when
 * storage is refused.
 */
export function createUnresolvedAttemptStore<A extends StoredAttempt<{ idempotency_key: string }>>(
  storage: AttemptStorage | null,
  storageKey: string,
  isAttempt: (value: unknown) => value is A,
) {
  const memory = new Map<string, A>();

  const readAll = (): Record<string, unknown> => {
    try {
      const raw = storage?.getItem(storageKey);
      const parsed: unknown = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  };

  const writeAll = (all: Record<string, unknown>) => {
    try {
      if (Object.keys(all).length) storage?.setItem(storageKey, JSON.stringify(all));
      else storage?.removeItem(storageKey);
    } catch {
      return;
    }
  };

  return {
    get(organizationId: string): A | null {
      const stored = readAll()[organizationId];
      return isAttempt(stored) ? stored : (memory.get(organizationId) ?? null);
    },
    /**
     * Removes the attempt only while it is still the one sent with `idempotencyKey`: another tab may have
     * stored its own since, and settling this one must never erase that one.
     */
    clear(organizationId: string, idempotencyKey: string): void {
      const all = readAll();
      const stored = all[organizationId];
      if (isAttempt(stored) && stored.payload.idempotency_key === idempotencyKey) {
        delete all[organizationId];
        writeAll(all);
      }
      if (memory.get(organizationId)?.payload.idempotency_key === idempotencyKey)
        memory.delete(organizationId);
    },
    /**
     * Calls `listener` when another tab of this browser changes the store (the `storage` event never fires in
     * the writer).
     */
    subscribe(listener: () => void): () => void {
      if (typeof window === "undefined") return () => undefined;
      const onStorage = (event: StorageEvent) => {
        if (event.key === storageKey || event.key === null) listener();
      };
      window.addEventListener("storage", onStorage);
      return () => window.removeEventListener("storage", onStorage);
    },
    set(organizationId: string, attempt: A | null): void {
      const all = readAll();
      if (attempt) {
        memory.set(organizationId, attempt);
        all[organizationId] = attempt;
      } else {
        memory.delete(organizationId);
        delete all[organizationId];
      }
      writeAll(all);
    },
  };
}

/** `localStorage`, or null where the browser refuses it (the stores then live in memory). */
export const browserAttemptStorage = (): AttemptStorage | null => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
};
